import { Action, Dispatch } from 'redux';
import { History } from 'history';
import { ThunkDispatch } from 'redux-thunk';
import { AxiosError } from 'axios';
import i18n from 'i18n';
import { RootState } from 'data/root.reducers';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { showSuccessMessage, showError } from 'shared/toaster';
import { SYSTEM_CONFIGURATION_ROUTE, NETWORK, TARGET_DOMAIN } from '../../constants';
import { extractErrorMessage } from '../../utils';
import * as api from './targetDomain.api';
import {
  TargetDomain,
  GetTargetDomainListAction,
  GetSingleTargetDomainAction,
  CreateTargetDomainThunkAction,
  DeleteTargetDomainThunkAction,
  SelectTargetDomain,
  UpdateTargetDomainThunkAction,
  GET_TARGET_DOMAIN_LIST,
  GET_TARGET_DOMAIN,
  CREATE_TARGET_DOMAIN,
  UPDATE_TARGET_DOMAIN,
  DELETE_TARGET_DOMAIN,
  SELECT_TARGET_DOMAIN,
  GO_TO_TARGET_DOMAIN_CREATE,
  GO_TO_TARGET_DOMAIN_EDIT,
  GO_TO_TARGET_DOMAIN_VIEW,
  GO_TO_TARGET_DOMAIN_LIST,
} from './targetDomain.types';
import { getSelectedTargetDomain } from './targetDomain.selectors';

export function getTargetDomainsList(deliveryFunctionId: string): GetTargetDomainListAction {
  return {
    type: GET_TARGET_DOMAIN_LIST,
    payload: api.getTargetDomainList(deliveryFunctionId),
  };
}

export function getTargetDomain(deliveryFunctionId: string, targetDomainId: string): GetSingleTargetDomainAction {
  return {
    type: GET_TARGET_DOMAIN,
    payload: api.getTargetDomain(deliveryFunctionId, targetDomainId),
  };
}

export function createTargetDomain(
  deliveryFunctionId: string,
  body: WithAuditDetails<TargetDomain>,
  history?: History,
): CreateTargetDomainThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_TARGET_DOMAIN,
        payload: api.createTargetDomain(deliveryFunctionId, body),
      });

      if (history) {
        dispatch(goToTargetDomainList(history));
      } else {
        dispatch(getTargetDomainsList(deliveryFunctionId));
      }

      showSuccessMessage(i18n.t('targetDomain.create.success', { name: body.data.domain }));

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('targetDomain.create.failed', { name: body.data.domain, error: errorMessage }));
    }
  };
}

export function updateTargetDomain(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<TargetDomain>,
  history: History,
): UpdateTargetDomainThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_TARGET_DOMAIN,
        payload: api.updateTargetDomain(deliveryFunctionId, id, body),
      });

      dispatch(goToTargetDomainList(history));
      showSuccessMessage(i18n.t('targetDomain.edit.success', { name: body.data.domain }));

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('targetDomain.edit.failed', { name: body.data.domain, error: errorMessage }));
    }
  };
}

export function deleteTargetDomain(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteTargetDomainThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedTargetDomain] = getSelectedTargetDomain(getState());

    try {
      await dispatch({
        type: DELETE_TARGET_DOMAIN,
        payload: api.deleteTargetDomain(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('targetDomain.delete.success', { name: selectedTargetDomain?.domain }), 5000);
      if (history != null) {
        dispatch(goToTargetDomainList(history));
      }
    } catch (e) {
      showError(i18n.t('targetDomain.delete.failed', { name: selectedTargetDomain?.domain }));

    } finally {
      dispatch(getTargetDomainsList(deliveryFunctionId));
    }
  };
}

export function selectTargetDomain(
  targetDomain: TargetDomain[],
): SelectTargetDomain {
  return {
    type: SELECT_TARGET_DOMAIN,
    payload: targetDomain,
  };
}

export function goToTargetDomainList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_TARGET_DOMAIN_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${TARGET_DOMAIN}`);
  };
}

export function goToTargetDomainCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_TARGET_DOMAIN_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${TARGET_DOMAIN}/create`);
  };
}

export function goToTargetDomainEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_TARGET_DOMAIN_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${TARGET_DOMAIN}/edit`);
  };
}

export function goToTargetDomainView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_TARGET_DOMAIN_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${NETWORK}${TARGET_DOMAIN}/view`);
  };
}
